import { useState } from "react";
import type { FormEvent } from "react";
import { useParams } from "react-router";
import { Aviso, Botao, Campo, CampoLongo } from "../componentes/ui";
import { CascaPublica } from "../componentes/layout";
import { ErroDaApi } from "../nucleo/api";
import {
  esquemaDoPedidoPublico,
  useAbrirChamadoPublico,
  useFormularioPublico,
} from "../nucleo/canais";
import { useTitulo } from "../nucleo/titulo";

function mensagemDaRecusa(erro: unknown): string {
  if (erro instanceof ErroDaApi) {
    if (erro.status === 429) {
      return "Recebemos pedidos demais deste endereço em pouco tempo. Aguarde alguns minutos e tente de novo.";
    }
    if (erro.status === 404) return "Este link de atendimento não vale mais.";
    if (erro.status === 422 && erro.message) return erro.message;
    if (erro.status === null) {
      return "Não foi possível falar com o portal. Verifique a conexão e tente de novo.";
    }
  }
  return "Não foi possível enviar o seu pedido agora. Tente de novo em instantes.";
}

type Erros = Partial<Record<"nome" | "email" | "assunto" | "descricao", string>>;

/** O formulário público de abertura de chamado: sem login, pelo link que a GEOvendas entrega. */
export function AbrirChamadoPublico() {
  useTitulo("Pedir atendimento");
  const { token = "" } = useParams();
  const formulario = useFormularioPublico(token);
  const abrir = useAbrirChamadoPublico(token);
  const [campos, setCampos] = useState({ nome: "", email: "", assunto: "", descricao: "" });
  // Isca anti-robô: a pessoa nunca vê este campo; programa que preenche tudo o preenche.
  const [isca, setIsca] = useState("");
  const [erros, setErros] = useState<Erros>({});

  if (formulario.isError) {
    return (
      <CascaPublica titulo="Este link não vale mais" descricao="O endereço de atendimento que você abriu não existe ou foi desativado.">
        <div data-testid="formulario-invalido">
          <Aviso tom="atencao">Peça um link novo a quem atende a sua empresa na GEOvendas.</Aviso>
        </div>
      </CascaPublica>
    );
  }

  if (abrir.isSuccess) {
    return (
      <CascaPublica titulo="Pedido recebido" descricao={formulario.data ? `Atendimento ${formulario.data.organizacao}` : undefined}>
        <div data-testid="pedido-recebido">
          <Aviso tom="sucesso" data-testid="mensagem-de-sucesso">{abrir.data.mensagem}</Aviso>
          <p className="nota-rodape mt-3">
            Guarde o número do chamado. Se a equipe precisar de algo, o retorno vem para o e-mail que você
            informou.
          </p>
        </div>
      </CascaPublica>
    );
  }

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const analise = esquemaDoPedidoPublico.safeParse(campos);
    if (!analise.success) {
      const novos: Erros = {};
      for (const problema of analise.error.issues) {
        const chave = problema.path[0] as keyof Erros;
        novos[chave] ??= problema.message;
      }
      setErros(novos);
      return;
    }
    setErros({});
    abrir.mutate({ ...analise.data, site: isca });
  }

  function mudar(chave: keyof typeof campos, valor: string) {
    setCampos((atual) => ({ ...atual, [chave]: valor }));
  }

  return (
    <CascaPublica
      titulo="Pedir atendimento"
      descricao={
        formulario.data
          ? `${formulario.data.nome} — atendimento ${formulario.data.organizacao}`
          : "Carregando o formulário…"
      }
    >
      <form onSubmit={submeter} data-testid="formulario-publico" noValidate>
        <Campo rotulo="Seu nome" value={campos.nome} onChange={(e) => mudar("nome", e.target.value)} erro={erros.nome} autoComplete="name" obrigatorio />
        <Campo rotulo="Seu e-mail" type="email" value={campos.email} onChange={(e) => mudar("email", e.target.value)} erro={erros.email} autoComplete="email" obrigatorio ajuda="Por aqui a equipe retorna para você." />
        <Campo rotulo="Assunto" value={campos.assunto} onChange={(e) => mudar("assunto", e.target.value)} erro={erros.assunto} obrigatorio />
        <CampoLongo rotulo="O que está acontecendo?" value={campos.descricao} rows={6} onChange={(e) => mudar("descricao", e.target.value)} erro={erros.descricao} obrigatorio />
        <div className="sr-only" aria-hidden="true">
          <label>
            Não preencha este campo
            <input
              type="text"
              name="site"
              tabIndex={-1}
              autoComplete="off"
              value={isca}
              onChange={(e) => setIsca(e.target.value)}
              data-testid="isca"
            />
          </label>
        </div>
        {abrir.isError ? <Aviso tom="erro" className="mb-3" data-testid="erro-do-envio">{mensagemDaRecusa(abrir.error)}</Aviso> : null}
        <Botao type="submit" disabled={abrir.isPending || formulario.isPending} className="botao--total">
          {abrir.isPending ? "Enviando…" : "Enviar pedido"}
        </Botao>
      </form>
    </CascaPublica>
  );
}
